import { ReactElement } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';
import lowerCase from 'lodash-es/lowerCase';
import localFont from 'next/font/local';
import Head from 'next/head';
import Script from 'next/script';

import { JabraThemeProvider, useJabraThemesList } from '@gn-audio/jss-components';

import { CondensedMenuProvider } from 'common/condensedMenu/CondensedMenuProvider';
import { IJabraTheme } from 'common/jabraTheme/JabraTheme.models';
import { PageProductProvider } from 'common/PageProductContext/pageProductContext';
import { ScrollerProvider } from 'common/scrolling/scrollerContext';
import { TopSection } from 'components/TopSection';
import Scripts from 'src/Scripts';
import { sanitizeScriptContent } from 'utils/sanitizeScriptContent';

import { LayoutProps, RouteFields } from './LayoutTypes';
import { PageMetaTags } from './PageMetaTags';
import { SpotWrapper } from './SpotWrapper';

export const GNElliotWeb = localFont({
  src: [
    {
      path: './fonts/GNElliotWeb-Light.woff2',
      weight: '300',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Regular.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Bold.woff2',
      weight: '600',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Bold.woff2',
      weight: '700',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Heavy.woff2',
      weight: '900',
      style: 'normal',
    },
  ],
});

const Layout = ({
  layoutData,
  headLinks,
  publicUrl,
  appSettings,
  filteredSelectedSites,
}: LayoutProps): React.JSX.Element => {
  const productNotAvailable = '/product-not-available';
  const { route, context } = layoutData.sitecore;

  const jabraThemes = useJabraThemesList();

  const fields = route?.fields as RouteFields;

  const jabraSitecoreTheme = jabraThemes.find(
    (theme) => lowerCase(theme?.name) === lowerCase(fields.Theme?.value)
  );

  const jabraTheme: IJabraTheme | undefined = jabraSitecoreTheme
    ? {
        backgroundColor: jabraSitecoreTheme.backgroundColor?.value,
        foregroundColor: jabraSitecoreTheme.foregroundColor?.value,
        darkTheme: !!jabraSitecoreTheme.darkTheme?.value,
      }
    : undefined;

  const isSmoothScrollEnabled = fields?.EnableSmoothScroll?.value || false;
  const isPageFullWidth = fields?.PageFullWidth?.value || false;
  const isProductAvailable = context.itemPath?.includes(productNotAvailable) ? false : true;

  return (
    <>
      <PageProductProvider>
        <Scripts />

        {!isProductAvailable ? (
          <Head>
            <meta name="robots" content="noindex" />
            <meta name="prerender-status-code" content="404" />
          </Head>
        ) : (
          <>
            <TopSection nodes={appSettings?.topSectionParsed} />
            <Head>
              {headLinks.map((headLink) => (
                <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
              ))}
            </Head>
            <PageMetaTags
              fields={fields}
              currentLanguage={context.language || ''}
              selectedLanguages={appSettings?.selectedLanguages || []}
              selectedRegions={appSettings?.selectedRegions || []}
              publicUrl={publicUrl || ''}
              filteredSelectedSites={filteredSelectedSites}
            />
          </>
        )}

        {appSettings?.topScripts.map((js) => {
          return (
            <Script
              id={js.id}
              key={js.id}
              dangerouslySetInnerHTML={{ __html: sanitizeScriptContent(js.script.value) }}
            />
          );
        })}
        {appSettings?.bottomScripts.map((js) => {
          return (
            <Script
              strategy="afterInteractive"
              id={js.id}
              key={js.id}
              dangerouslySetInnerHTML={{ __html: sanitizeScriptContent(js.script.value) }}
            />
          );
        })}
        {appSettings?.topSectionParsed
          ?.filter(({ tagName }) => tagName === 'script')
          .map(({ id, props, content }) => {
            return (
              <Script
                key={id}
                {...props}
                dangerouslySetInnerHTML={{ __html: sanitizeScriptContent(content ?? '') }}
              />
            );
          })}
        {appSettings?.bottomSectionParsed
          ?.filter(({ tagName }) => tagName === 'script')
          .map(({ id, props, content }) => {
            return (
              <Script
                key={id}
                strategy="afterInteractive"
                {...props}
                dangerouslySetInnerHTML={{ __html: sanitizeScriptContent(content ?? '') }}
              />
            );
          })}
        <CondensedMenuProvider>
          <JabraThemeProvider theme={jabraTheme}>
            <div
              id="page-container-wrapper"
              className={isPageFullWidth ? 'm-auto' : 'm-auto max-w-[1920px]'}
            >
              <ScrollerProvider isSmoothScrollEnabled={isSmoothScrollEnabled}>
                <div
                  className={`page-container ${isPageFullWidth ? 'w-full max-w-none' : ''} ${GNElliotWeb.className} ${RemoveScroll.classNames.zeroRight}`}
                  data-brand="jabra"
                >
                  {/* root placeholder for the app, which we add components to using route data */}
                  <div>
                    {route && route?.placeholders['jss-header'] && (
                      <Placeholder name="jss-header" rendering={route} disableSuspense />
                    )}
                  </div>

                  <main>
                    {route && route?.placeholders['jss-main'] && (
                      <Placeholder
                        name="jss-main"
                        rendering={route}
                        disableSuspense
                        render={(components, renderings) => {
                          return (
                            <div className="flex flex-col">
                              {components.map((component: ReactElement, idx) => {
                                const rendering = renderings[idx];
                                if (!('componentName' in rendering)) {
                                  return component;
                                }
                                return (
                                  <SpotWrapper
                                    key={`${rendering.uid}-${idx}`}
                                    component={component}
                                    rendering={rendering}
                                  />
                                );
                              })}
                            </div>
                          );
                        }}
                      />
                    )}
                  </main>
                  <div>
                    {route && route?.placeholders['footer'] && (
                      <Placeholder name="footer" rendering={route} disableSuspense />
                    )}
                  </div>
                </div>
              </ScrollerProvider>
            </div>
            {appSettings?.bottomSectionParsed
              ?.filter(({ tagName }) => tagName === 'style')
              .map((node) => {
                return (
                  <style
                    key={node.id + node.tagName}
                    {...node.props}
                    dangerouslySetInnerHTML={{ __html: sanitize(node.content ?? '') }}
                  />
                );
              })}
          </JabraThemeProvider>
        </CondensedMenuProvider>
      </PageProductProvider>
    </>
  );
};
export default Layout;
